import { useCallback, useState } from "react";
import { writeContract, readContract } from "@wagmi/core";
import useAccount from "@/hooks/useAccount";
import { useSwitchNetwork, useNetwork } from "wagmi";
import { toast } from "react-toastify";
import { waitForTransaction } from "wagmi/actions";
import { rewardContractInfo, stakingContractInfo } from "@/services/contract";
import { isUserRejectError } from "@/utils/validate";
import events from "@/services/events";
import type { StakingKATRewardWithMeta } from "@/types/network";
import { isSkipError } from "@/utils/validate";

export default function useRewardContract() {
  const { address, login } = useAccount();
  const { switchNetworkAsync } = useSwitchNetwork();
  const { chain } = useNetwork();
  const [claiming, setClaiming] = useState(false);

  const claimClaimer = useCallback(
    async (epoch: number, reward: StakingKATRewardWithMeta) => {
      if (!address) {
        return login();
      }
      if (claiming) {
        return;
      }
      setClaiming(true);
      try {
        if (!switchNetworkAsync) {
          return;
        }
        if (chain?.id !== rewardContractInfo.chainId) {
          await switchNetworkAsync(rewardContractInfo.chainId);
        }
        const resp = await writeContract({
          ...rewardContractInfo,
          functionName: "claimClaimer",
          args: [epoch],
        });
        try {
          await waitForTransaction({
            hash: resp.hash,
            confirmations: 6,
          });
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }

        events.emit("claimClaimer", {
          epoch,
          address,
          reward,
        });
      } catch (e: any) {
        if (isUserRejectError(e)) {
          return;
        }
        console.error(e);
        toast.error(e.message);
        throw new Error(e.message);
      } finally {
        setClaiming(false);
      }
    },
    [chain, switchNetworkAsync, claiming, address, login]
  );

  const claimValidator = useCallback(
    async (validatorId: string, reward: StakingKATRewardWithMeta) => {
      if (!address) {
        return login();
      }
      if (claiming) {
        return;
      }
      setClaiming(true);
      try {
        if (!switchNetworkAsync) {
          return;
        }
        if (chain?.id !== rewardContractInfo.chainId) {
          await switchNetworkAsync(rewardContractInfo.chainId);
        }
        const resp = await writeContract({
          ...rewardContractInfo,
          functionName: "claimValidator",
          args: [parseInt(validatorId)],
        });
        try {
          await waitForTransaction({
            hash: resp.hash,
            confirmations: 6,
          });
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }

        events.emit("claimValidator", {
          validatorId,
          address,
          reward,
        });
      } catch (e: any) {
        if (isUserRejectError(e)) {
          return;
        }
        console.error(e);
        toast.error(e.message);
        throw new Error(e.message);
      } finally {
        setClaiming(false);
      }
    },
    [chain, switchNetworkAsync, claiming, address, login]
  );

  const claimLieutenant = useCallback(
    async (epochs: number[], reward: StakingKATRewardWithMeta) => {
      if (!address) {
        return login();
      }
      if (claiming) {
        return;
      }
      setClaiming(true);
      try {
        if (!switchNetworkAsync) {
          return;
        }
        if (chain?.id !== rewardContractInfo.chainId) {
          await switchNetworkAsync(rewardContractInfo.chainId);
        }
        const resp = await writeContract({
          ...rewardContractInfo,
          functionName: "claimLieutenant",
          args: [epochs.slice(0, 10), 0, 0],
        });
        try {
          await waitForTransaction({
            hash: resp.hash,
            confirmations: 6,
          });
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }
        events.emit("claimLieutenant", {
          epochs,
          address,
          reward,
        });
      } catch (e: any) {
        if (isUserRejectError(e)) {
          return;
        }
        console.error(e);
        toast.error(e.message);
        throw new Error(e.message);
      } finally {
        setClaiming(false);
      }
    },
    [chain, switchNetworkAsync, claiming, address, login]
  );

  const claimStakerBatch = useCallback(
    async (tokenIds: string[]) => {
      if (tokenIds.length === 0) {
        return;
      }
      if (!address) {
        return login();
      }
      if (claiming) {
        return;
      }
      setClaiming(true);
      try {
        if (!switchNetworkAsync) {
          return;
        }
        if (chain?.id !== rewardContractInfo.chainId) {
          await switchNetworkAsync(rewardContractInfo.chainId);
        }

        const resp = await writeContract({
          ...rewardContractInfo,
          functionName: "claimStakerBatch",
          args: [
            tokenIds.map((id) => {
              return Number(id);
            }),
          ],
        });
        try {
          await waitForTransaction({
            hash: resp.hash,
            confirmations: 6,
          });
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }
        events.emit("claimStakerBatch", {
          tokenIds,
          address,
        });
        toast.success("Claim success");
        return true;
      } catch (e: any) {
        if (isUserRejectError(e)) {
          return;
        }
        console.error(e);
        toast.error(e.message);
        throw new Error(e.message);
      } finally {
        setClaiming(false);
      }
    },
    [chain, switchNetworkAsync, claiming, address, login]
  );

  return {
    claimStakerBatch,
    claiming,
    claimClaimer,
    claimValidator,
    claimLieutenant,
  };
}
